/**
 * Point-lens microlensing, as pure functions (no DOM, no WebGL).
 * Angles are in radians on a small patch of sky. A lens of Einstein radius thetaE remaps the sky as
 *   beta = theta - thetaE^2 (theta - L) / |theta - L|^2
 * and a point source at angular distance u * thetaE from the lens is magnified by A(u).
 */

export interface Vec2 { x: number; y: number }
export interface LensBody extends Vec2 { vx: number; vy: number }
export interface LensWithRadius extends LensBody { thetaE: number }

/** Horizontal field of view of the chapter, radians. */
export const FOV = 0.5;
/** Half-extent of the field the targets live in, radians (16:9 at FOV 0.5). */
export const FIELD_X = 0.25;
export const FIELD_Y = 0.14;
/**
 * The box the target stars live in (radians), set in from the field so the HUD never hides a flare: below the top
 * bar, above the bottom band, and the bottom row starts right of the text column in the lower-left corner.
 */
export const TARGET_BOX = { x0: -0.21, x1: 0.21, y0: -0.05, y1: 0.105, bottomMinX: -0.05 };
/** A star counts as flaring above this magnification. */
export const FLARE_A = 1.5;
/** A click this close (radians) to a flaring star is a catch. */
export const CATCH_RADIUS = 0.012;
/** The mass must stay on a flaring star this long (seconds) before the discovery lands. */
export const HOLD_SECONDS = 0.7;

/** Magnification of a point source at separation u (in Einstein radii) from a point lens. */
export function magnification(u: number): number {
  const a = Math.abs(u);
  if (a === 0) return Infinity;
  if (!Number.isFinite(a) || a > 1e6) return 1;
  return (a * a + 2) / (a * Math.sqrt(a * a + 4));
}

/** Where the sky is sampled for a ray arriving from direction theta, for one point lens. Mirrors the shader. */
export function remapOne(theta: readonly [number, number], lens: readonly [number, number], thetaE: number): [number, number] {
  const dx = theta[0] - lens[0], dy = theta[1] - lens[1];
  const k = (thetaE * thetaE) / Math.max(dx * dx + dy * dy, 1e-8);
  return [theta[0] - k * dx, theta[1] - k * dy];
}

/**
 * Velocity that carries a lens in a straight line past a target at the given impact parameter
 * (the distance of closest approach). The line is rotated off the lens-to-target direction by asin(impact / distance),
 * so the miss distance is exactly the impact parameter. side picks which flank the lens passes on.
 */
export function aimLens(lens: Vec2, target: Vec2, speed: number, impact: number, side: 1 | -1 = 1): { vx: number; vy: number } {
  const dx = target.x - lens.x, dy = target.y - lens.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-12) return { vx: speed, vy: 0 };
  const phi = side * Math.asin(Math.min(1, Math.abs(impact) / dist));
  const c = Math.cos(phi), s = Math.sin(phi);
  const ex = dx / dist, ey = dy / dist;
  return { vx: speed * (ex * c - ey * s), vy: speed * (ex * s + ey * c) };
}

/** Time (>= 0) and distance of closest approach of a lens on a straight path to a target. */
export function closestApproach(lens: LensBody, target: Vec2): { t: number; d: number } {
  const v2 = lens.vx * lens.vx + lens.vy * lens.vy;
  const dx = target.x - lens.x, dy = target.y - lens.y;
  if (v2 === 0) return { t: 0, d: Math.hypot(dx, dy) };
  const t = Math.max(0, (dx * lens.vx + dy * lens.vy) / v2);
  return { t, d: Math.hypot(dx - lens.vx * t, dy - lens.vy * t) };
}

/** Wrap a coordinate into [-half, half]. Values already inside are returned untouched. */
export function wrapCoord(v: number, half: number): number {
  if (v >= -half && v <= half) return v;
  const span = 2 * half;
  return ((((v + half) % span) + span) % span) - half;
}

/** Advance a lens by dt seconds along its velocity, wrapping around the bounds (half-extents). Returns a new lens. */
export function stepLens(lens: LensBody, dt: number, bounds: Vec2): LensBody {
  return {
    x: wrapCoord(lens.x + lens.vx * dt, bounds.x),
    y: wrapCoord(lens.y + lens.vy * dt, bounds.y),
    vx: lens.vx,
    vy: lens.vy
  };
}

/** The lens with the smallest u = distance / thetaE for a target, and the magnification it gives. */
export function nearestLens(target: Vec2, lenses: ReadonlyArray<Vec2 & { thetaE: number }>): { index: number; u: number; A: number } {
  let index = -1, best = Infinity;
  for (let i = 0; i < lenses.length; i++) {
    const e = lenses[i].thetaE;
    if (!(e > 1e-9)) continue;
    const u = Math.hypot(target.x - lenses[i].x, target.y - lenses[i].y) / e;
    if (u < best) { best = u; index = i; }
  }
  return { index, u: best, A: index < 0 ? 1 : magnification(best) };
}

/** Which star the mass is holding on, and for how long (seconds). target is -1 when none is flaring. */
export interface HoldState { target: number; t: number }

/**
 * Advance the hold timer by one frame. `target` is the star with the highest magnification (-1 for none) and `A` its
 * magnification. The timer runs while that star is above the flare threshold and restarts when the star changes or the
 * magnification falls back under the threshold. Returns true once the same star has been held for `need` seconds.
 */
export function stepHold(state: HoldState, target: number, A: number, dt: number, threshold = FLARE_A, need = HOLD_SECONDS): boolean {
  if (target < 0 || !(A > threshold)) { state.target = -1; state.t = 0; return false; }
  if (target !== state.target) { state.target = target; state.t = 0; }
  state.t += dt;
  return state.t >= need;
}

/**
 * A quiet place to put the mass at the start: the point nearest the centre of the field that is at least `clear` away
 * from every star. A grid search inside the target box, so it is the same every time. If no point is that clear
 * (a very crowded field), the point farthest from its nearest star is returned instead.
 */
export function quietSpot(targets: readonly Vec2[], clear: number, box = TARGET_BOX, step = 0.002): Vec2 {
  let best: Vec2 = { x: 0, y: 0 }, bestFromCentre = Infinity;
  let fallback: Vec2 = { x: 0, y: 0 }, fallbackClear = -Infinity;
  for (let y = box.y0; y <= box.y1 + 1e-12; y += step) {
    for (let x = box.x0; x <= box.x1 + 1e-12; x += step) {
      let nearest = Infinity;
      for (const T of targets) nearest = Math.min(nearest, Math.hypot(T.x - x, T.y - y));
      if (nearest > fallbackClear) { fallbackClear = nearest; fallback = { x, y }; }
      const fromCentre = Math.hypot(x, y);
      if (nearest >= clear && fromCentre < bestFromCentre) { bestFromCentre = fromCentre; best = { x, y }; }
    }
  }
  return bestFromCentre < Infinity ? best : fallback;
}

/** Screen position in CSS pixels (origin top-left, y down) to the sky angle the shader uses (centred, y up). */
export function clickToTheta(x: number, y: number, cssW: number, cssH: number, fov: number, offset: Vec2): Vec2 {
  const w = Math.max(1, cssW), h = Math.max(1, cssH);
  return { x: ((x - 0.5 * w) / w) * fov + offset.x, y: ((0.5 * h - y) / w) * fov + offset.y };
}

/** Small seeded generator (mulberry32). */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface TargetField {
  pos: Vec2[];
  /** peak brightness of each star, 1.5 to 3.0 */
  peak: number[];
  /** 0 = blue-white, 1 = gold */
  tint: number[];
}

/** Fixed star positions: a jittered grid so they spread evenly over the box and never crowd one another. */
export function makeTargets(seed: number, cols = 6, rows = 4, box = TARGET_BOX): TargetField {
  const rng = makeRng(seed);
  const out: TargetField = { pos: [], peak: [], tint: [] };
  for (let r = 0; r < rows; r++) {
    const x0 = r === 0 ? box.bottomMinX : box.x0;   // row 0 is the bottom row
    for (let c = 0; c < cols; c++) {
      const fx = (c + 0.18 + 0.64 * rng()) / cols, fy = (r + 0.18 + 0.64 * rng()) / rows;
      out.pos.push({ x: x0 + (box.x1 - x0) * fx, y: box.y0 + (box.y1 - box.y0) * fy });
      out.peak.push(1.5 + 1.5 * rng());
      out.tint.push(rng());
    }
  }
  return out;
}

export interface PlanOptions {
  /** speed of the aimed lens, rad/s */
  speed: number;
  /** a relocated lens re-forms this far from its target, rad */
  approach: number;
  /** a lens already closer than this to its target is aimed in place, rad */
  nearMax: number;
  /** wrap half-extents the lens must stay inside */
  bounds: Vec2;
  /** impact parameter range, as fractions of thetaE (the brief asks for under 0.5) */
  minImpact: number;
  maxImpact: number;
}

export interface Encounter {
  lens: number;
  target: number;
  /** where the lens must re-form first, or null to aim it from where it is */
  start: Vec2 | null;
  vx: number;
  vy: number;
  /** distance of closest approach, rad */
  impact: number;
}

/**
 * Choose a target and a lens, and a velocity that will bring the lens past the target at a small impact parameter.
 * The nearest eligible lens is used. If it is far away it is relocated to a start point near the target, so the
 * flare arrives in seconds rather than minutes.
 */
export function planEncounter(
  rng: () => number,
  targets: readonly Vec2[],
  lenses: readonly LensWithRadius[],
  opt: PlanOptions,
  eligibleTargets?: readonly number[],
  eligibleLenses?: readonly number[]
): Encounter | null {
  const tIds = eligibleTargets ?? targets.map((_, i) => i);
  const lIds = eligibleLenses ?? lenses.map((_, i) => i);
  if (tIds.length === 0 || lIds.length === 0) return null;
  const target = tIds[Math.min(tIds.length - 1, Math.floor(rng() * tIds.length))];
  const T = targets[target];
  let lens = lIds[0], bd = Infinity;
  for (const k of lIds) {
    const d = Math.hypot(T.x - lenses[k].x, T.y - lenses[k].y);
    if (d < bd) { bd = d; lens = k; }
  }
  const L = lenses[lens];
  const impact = (opt.minImpact + (opt.maxImpact - opt.minImpact) * rng()) * L.thetaE;
  const side: 1 | -1 = rng() < 0.5 ? -1 : 1;
  let start: Vec2 | null = null;
  if (bd > opt.nearMax) {
    for (let tries = 0; tries < 12 && !start; tries++) {
      const a = rng() * Math.PI * 2;
      const sx = T.x + opt.approach * Math.cos(a), sy = T.y + opt.approach * Math.sin(a);
      if (Math.abs(sx) <= opt.bounds.x && Math.abs(sy) <= opt.bounds.y) start = { x: sx, y: sy };
    }
    if (!start) {
      const m = Math.hypot(T.x, T.y) || 1;
      start = { x: T.x - (opt.approach * T.x) / m, y: T.y - (opt.approach * T.y) / m };
    }
  }
  const v = aimLens(start ?? L, T, opt.speed, impact, side);
  return { lens, target, start, vx: v.vx, vy: v.vy, impact };
}
